import axios, { type AxiosResponse, AxiosError } from "axios";
import { attachRateLimitRetry } from "./withRateLimitRetry";

export interface CreateLearningMaterialDto {
  topicId: string;
  title: string;
  description?: string;
  tags?: string[];
  category?: string;
  subCategory?: string;
  difficulty?: string;
  allowDownload?: boolean;
  drmEnabled?: boolean;
  isActive?: boolean;
}

export type UpdateLearningMaterialDto = Partial<CreateLearningMaterialDto>;

/**
 * What a progress heartbeat sends. Split from the response shape on purpose:
 * the server derives `completionPercentage` and `isCompleted` from the position
 * and the file's duration, and ignores them if a client sends them.
 */
export interface UpdateMaterialProgressDTO {
  lastPosition?: number;
  watchTime?: number;
}

export interface FileUpload {
  id: string;
  filename: string;
  fileType: string;
  status: string;
  s3Key?: string;
  duration?: number;
  thumbnailUrl?: string;
  hlsManifestUrl?: string;
}

export interface Test {
  id: string;
  title: string;
  timeLimit?: number;
  type: string;
  difficulty?: string;
  passingScore?: number;
}

export interface Topic {
  id: string;
  name: string;
  courseId: string;
}

export interface MaterialProgress {
  completionPercentage: number;
  isCompleted: boolean;
  lastPosition?: number;
  watchTime?: number;
}

export interface LearningMaterial {
  id: string;
  topicId: string;
  title: string;
  description?: string;
  displayOrder: number;
  tags: string[];
  category?: string;
  subCategory?: string;
  difficulty?: string;
  allowDownload: boolean;
  drmEnabled: boolean;
  isActive: boolean;
  prevMaterialId?: string;
  nextMaterialId?: string;
  fileUploadId?: string;
  createdAt: string;
  updatedAt: string;
  topic: Topic;
  fileUpload?: FileUpload;
  test?: Test;
  prevMaterial?: { id: string; title: string };
  nextMaterial?: { id: string; title: string };
  materialProgress?: MaterialProgress[];
}

export interface ApiResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
}

export interface ApiError {
  message: string;
  statusCode: number;
  error?: string;
}

// ============================================================================
// AXIOS CONFIGURATION
// ============================================================================

const API_BASE_URL = import.meta.env.VITE_BACKEND_URL;

const materialsApiClient = axios.create({
  baseURL: `${API_BASE_URL}/api/materials`,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Response interceptor for error handling
// Registered first so it sees the raw AxiosError (with response.status
// and Retry-After) before the mapper below reshapes it.
attachRateLimitRetry(materialsApiClient);

materialsApiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    const errorData = error.response?.data as
      | { message?: string; error?: string }
      | undefined;

    const apiError: ApiError = {
      message: errorData?.message || error.message || "An error occurred",
      statusCode: error.response?.status || 0,
      error: errorData?.error,
    };
    return Promise.reject(apiError);
  }
);

// ============================================================================
// LEARNING MATERIALS API FUNCTIONS
// ============================================================================
/**
 * Get a specific learning material by ID
 */
export async function getMaterial(
  materialId: string
): Promise<ApiResponse<LearningMaterial>> {
  if (!materialId.trim()) {
    throw {
      message: "Material ID is required",
      statusCode: 400,
    } as ApiError;
  }

  const response = await materialsApiClient.get<ApiResponse<LearningMaterial>>(
    `/${encodeURIComponent(materialId)}`
  );
  return response.data;
}

/**
 * Get all materials for a specific topic
 */
export async function getMaterialsByTopic(
  topicId: string
): Promise<ApiResponse<LearningMaterial[]>> {
  if (!topicId.trim()) {
    throw {
      message: "Topic ID is required",
      statusCode: 400,
    } as ApiError;
  }

  const response = await materialsApiClient.get<
    ApiResponse<LearningMaterial[]>
  >(`?topicId=${encodeURIComponent(topicId)}`);
  return response.data;
}

/**
 * Get materials with user progress information
 */
export async function getMaterialsWithProgress(
  topicId: string,
  userId: string
): Promise<ApiResponse<LearningMaterial[]>> {
  if (!topicId.trim()) {
    throw {
      message: "Topic ID is required",
      statusCode: 400,
    } as ApiError;
  }

  if (!userId.trim()) {
    throw {
      message: "User ID is required",
      statusCode: 400,
    } as ApiError;
  }

  const response = await materialsApiClient.get<
    ApiResponse<LearningMaterial[]>
  >(
    `/progress?topicId=${encodeURIComponent(topicId)}&userId=${encodeURIComponent(userId)}`
  );
  return response.data;
}

/**
 * Get the next learning material in the sequence
 */
export async function getNextMaterial(
  currentMaterialId: string
): Promise<ApiResponse<LearningMaterial | null>> {
  if (!currentMaterialId.trim()) {
    throw {
      message: "Current material ID is required",
      statusCode: 400,
    } as ApiError;
  }

  const response = await materialsApiClient.get<
    ApiResponse<LearningMaterial | null>
  >(`/${encodeURIComponent(currentMaterialId)}/next`);
  return response.data;
}

/**
 * Get the previous learning material in the sequence
 */
export async function getPrevMaterial(
  currentMaterialId: string
): Promise<ApiResponse<LearningMaterial | null>> {
  if (!currentMaterialId.trim()) {
    throw {
      message: "Current material ID is required",
      statusCode: 400,
    } as ApiError;
  }

  const response = await materialsApiClient.get<
    ApiResponse<LearningMaterial | null>
  >(`/${encodeURIComponent(currentMaterialId)}/prev`);
  return response.data;
}

/**
 * Update an existing learning material
 */
export async function updateMaterial(
  materialId: string,
  updateData: UpdateLearningMaterialDto
): Promise<ApiResponse<LearningMaterial>> {
  if (!materialId.trim()) {
    throw {
      message: "Material ID is required",
      statusCode: 400,
    } as ApiError;
  }

  if (!updateData || Object.keys(updateData).length === 0) {
    throw {
      message: "Update data is required",
      statusCode: 400,
    } as ApiError;
  }

  if (updateData.title && !updateData.title.trim()) {
    throw {
      message: "Title cannot be empty",
      statusCode: 400,
    } as ApiError;
  }

  if (updateData.topicId && !updateData.topicId.trim()) {
    throw {
      message: "Topic ID cannot be empty",
      statusCode: 400,
    } as ApiError;
  }

  const response = await materialsApiClient.put<ApiResponse<LearningMaterial>>(
    `/${encodeURIComponent(materialId)}`,
    updateData
  );
  return response.data;
}

/**
 * Delete a learning material
 */
export async function deleteMaterial(
  materialId: string
): Promise<ApiResponse<void>> {
  if (!materialId.trim()) {
    throw {
      message: "Material ID is required",
      statusCode: 400,
    } as ApiError;
  }

  const response = await materialsApiClient.delete<ApiResponse<void>>(
    `/${encodeURIComponent(materialId)}`
  );
  return response.data;
}

/**
 * Activate a learning material
 */
export async function activateMaterial(
  materialId: string
): Promise<ApiResponse<LearningMaterial>> {
  if (!materialId.trim()) {
    throw {
      message: "Material ID is required",
      statusCode: 400,
    } as ApiError;
  }

  const response = await materialsApiClient.patch<
    ApiResponse<LearningMaterial>
  >(`/${encodeURIComponent(materialId)}/activate`);
  return response.data;
}


/**
 * Update material progress for a user
 */
export async function updateMaterialProgress(
  materialId: string,
  progressData: UpdateMaterialProgressDTO
): Promise<ApiResponse<MaterialProgress>> {
  if (!materialId.trim()) {
    throw {
      message: "Material ID is required",
      statusCode: 400,
    } as ApiError;
  }

  if (!progressData || Object.keys(progressData).length === 0) {
    throw {
      message: "Progress data is required",
      statusCode: 400,
    } as ApiError;
  }

  const response = await materialsApiClient.put<ApiResponse<MaterialProgress>>(
    `/${encodeURIComponent(materialId)}/progress`,
    progressData
  );
  return response.data;
}

export async function eligibleForAssessment(courseId: string): Promise<ApiResponse<boolean>> {
  if (!courseId.trim()) {
    throw {
      message: "Course ID is required",
      statusCode: 400,
    } as ApiError;
  }

  const response = await materialsApiClient.get<
    ApiResponse<boolean>
  >(`/assessment/eligible/${courseId}`);
  return response.data;
}

/**
 * Get latest material progress for current user
 */
export async function getLatestMaterialProgress(
  limit?: number
): Promise<ApiResponse<LearningMaterial[]>> {
  const params = new URLSearchParams();
  if (limit) {
    params.append("limit", limit.toString());
  }

  const queryString = params.toString();
  const url = queryString
    ? `/latest/progress?${queryString}`
    : "/latest/progress";

  const response =
    await materialsApiClient.get<ApiResponse<LearningMaterial[]>>(url);
  return response.data;
}
